import { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../contexts/auth';
import { findSameCredentials, loginAsYourself } from '../services/api_switch_utenze';

/**
 * Switch Utenze logic (web port of the mobile `fetchSwitchUtenze` + `handleCompanySwitch`).
 *
 * - Fetches the alternative accounts once we have a token (analog of the mobile
 *   `useFocusEffect`). Any error hides the section silently, exactly like mobile.
 * - Switching gets a new session via `login-as-yourself`; that response IS the full
 *   employee profile (same shape as `/api/me` PLUS the `employee` object and
 *   `customer_type: 'employee'`), so we use it directly — exactly like the normal
 *   login flow in `login.jsx` and the mobile app. We must NOT re-fetch via
 *   `whoami`/`/api/me`: with the switched token, `/api/me` resolves back to the
 *   underlying consultant/company account (customer_type !== 'employee'), which is
 *   what previously made the app fall back to the company UI after a switch.
 */
export function useSwitchUtenze() {
  const { token, setToken, setProfile, setIsEmployee } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    findSameCredentials(token)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        // Same rule as mobile: on any error the section simply doesn't appear.
        if (!cancelled) setData(null);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const switchCompany = useCallback(
    async (companyId) => {
      setSwitching(true);
      setSwitchError(null);

      try {
        const newProfile = await loginAsYourself(token, companyId);
        if (!newProfile?.token) throw new Error('Missing token in login-as-yourself response');

        // The login-as-yourself response IS the canonical profile for the target
        // account (see login.jsx, which persists the login response verbatim). It
        // already carries `customer_type: 'employee'` and the full `employee` object,
        // so we trust it directly instead of round-tripping through `/api/me`.
        const employeeSession = newProfile.customer_type === 'employee';

        setToken(newProfile.token);
        setProfile(newProfile);
        setIsEmployee(employeeSession);

        // Guarantee the new session is persisted before the hard reload
        // (same localStorage keys the jotai atoms use — see stores/mainStore.jsx).
        localStorage.setItem('jwt', JSON.stringify(newProfile.token));
        localStorage.setItem('profile', JSON.stringify(newProfile));
        localStorage.setItem('isEmployee', JSON.stringify(employeeSession));

        // Full reload to home: resets DataContext globals and every component cache,
        // re-bootstrapping the app from the freshly persisted session.
        window.location.replace('/');
      } catch (e) {
        setSwitchError(e);
        setSwitching(false);
      }
    },
    [token, setToken, setProfile, setIsEmployee]
  );

  return {
    utenze: data?.employees ?? [],
    totalMatches: data?.total_matches ?? 0,
    switchCompany,
    switching,
    switchError,
  };
}
